import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { LandingPage } from './components/auth/LandingPage';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import ApprovalResultPage from './components/auth/ApprovalResultPage';
import { WorkLogHistoryRefactored as WorkLogHistory } from './components/community/WorkLogHistoryRefactored';
import { FeedbackHistory } from './components/feedback/FeedbackHistory';
import { ExcelDataDashboard } from './components/dashboard/ExcelDataDashboard';
import { MainLayout } from './components/layout/MainLayout';
import { Footer } from './components/ui/Footer';
import { Header } from './components/ui/Header';
import { authService } from './services/authService';
import { useCustomerStore } from './stores/customerStore';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

type Page = 'landing' | 'dashboard' | 'worklog' | 'feedback';
type AuthPage = 'landing' | 'login' | 'register';

function MainApp() {
  const [currentPage, setCurrentPage] = useState<Page>('landing');
  const [authPage, setAuthPage] = useState<AuthPage>('landing');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string>('');
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { setSelectedCustomer } = useCustomerStore();

  useEffect(() => {
    // 앱 시작 시 인증 상태 확인
    const authenticated = authService.isAuthenticated();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      const userInfo = authService.getUserInfo();
      if (userInfo) {
        setUsername(userInfo.username);
      }
    }
  }, []);

  const handleNavigate = (page: Page) => {
    setCurrentPage(page);
    // 페이지 전환 시 전체고객사 선택
    setSelectedCustomer('all');
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    const userInfo = authService.getUserInfo();
    if (userInfo) {
      setUsername(userInfo.username);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUsername('');
    setCurrentPage('landing');
  };

  const handleLogoClickWhenNotAuthenticated = () => {
    setAuthPage('landing');
  };

  if (!isAuthenticated) {
    return (
      <>
        <Header
          currentPage={'dashboard'}
          onNavigate={handleNavigate}
          username=""
          isAuthenticated={false}
          onLogoClick={handleLogoClickWhenNotAuthenticated}
        />
        <div style={{
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1,
          backgroundImage: 'url(/fast_bg.gif)',
          backgroundSize: '140%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          paddingTop: 'calc(88px + 2rem)'
        }}>
          {/* 검은색 그라데이션 오버레이 */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 1
          }} />

          <style>{`
            @keyframes cardFadeIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            .card-fade-in {
              animation: cardFadeIn 0.4s ease-out;
              position: relative;
              z-index: 2;
            }
          `}</style>
          <div className="card-fade-in" key={authPage}>
            {authPage === 'landing' ? (
              <LandingPage
                onLoginClick={() => setAuthPage('login')}
                onRegisterClick={() => setAuthPage('register')}
              />
            ) : authPage === 'login' ? (
              <LoginPage
                onLoginSuccess={handleLoginSuccess}
                onRegisterClick={() => setAuthPage('register')}
              />
            ) : (
              <RegisterPage
                onBackToLogin={() => setAuthPage('login')}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  // 로그인 후 landing 페이지인 경우
  if (currentPage === 'landing') {
    return (
      <>
        <Header
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          username={username}
          isDarkMode={isDarkMode}
          onToggleDarkMode={toggleDarkMode}
          isAuthenticated={true}
        />
        <div style={{
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1,
          backgroundImage: 'url(/fast_bg.gif)',
          backgroundSize: '140%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          paddingTop: 'calc(88px + 2rem)'
        }}>
          {/* 검은색 그라데이션 오버레이 */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 1
          }} />

          <div style={{ position: 'relative', zIndex: 2 }}>
            <LandingPage
              onLoginClick={() => {/* 이미 로그인됨 */}}
              onRegisterClick={() => {/* 이미 로그인됨 */}}
              isAuthenticated={true}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors">
      <Header
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        username={username}
        isDarkMode={isDarkMode}
        onToggleDarkMode={toggleDarkMode}
        isAuthenticated={true}
      />

      {/* Header가 fixed이므로 padding-top 추가 */}
      <div className="pt-[88px]">
        <MainLayout>
          {currentPage === 'dashboard' && <ExcelDataDashboard />}
          {currentPage === 'worklog' && <WorkLogHistory />}
          {currentPage === 'feedback' && <FeedbackHistory />}
        </MainLayout>
      </div>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/approve" element={<ApprovalResultPage />} />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
